import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  Check,
  Code2,
  Copy,
  Info,
  Loader,
  RotateCcw,
  Send,
  Undo2,
  UserRound,
} from 'lucide-react';
import { ConfirmationModal } from '../../ConfirmationModal';
import { PlayerSendModal } from '../PlayerSendModal';
import { TellrawPreview } from './TellrawPreview';
import { CompatToggles } from './CompatToggles';
import {
  FAMILY_LABELS,
  compatMisses,
  compileTellraw,
  parseTellraw,
  validateMessage,
} from '../../../utils/tellraw';
import type { Compat, TellrawMessage } from '../../../utils/tellraw/types';
import type { ComposerTurn } from '../../../types/scheduledCommands';
import { scheduledCommandsApi } from '../../../services/scheduledCommandsApi';
import { cn } from '../../../utils/cn';

/**
 * Where a tellraw is written: described to the assistant, refined with
 * follow-ups, checked in a faithful chat preview, and compiled for exactly the
 * versions switched on.
 *
 * The assistant answers in the run model (see utils/tellraw/types.ts) under a
 * strict schema on Mr. Roboto's side, and the command is compiled here by code,
 * never written by the model. That split is what makes the JSON correct every
 * time: the model decides what the message says and looks like, and the
 * compiler decides how each Minecraft version has to be told.
 */

export interface ComposerValue {
  message: TellrawMessage;
  compat: Compat;
  /** Who sees it on each server; `@a` for everyone. */
  selector: string;
  conversation: ComposerTurn[];
}

export const EMPTY_COMPOSER: ComposerValue = {
  message: { runs: [] },
  compat: { legacy: true, modern: true, current: true },
  selector: '@a',
  conversation: [],
};

interface TellrawComposerProps {
  value: ComposerValue;
  onChange: (next: ComposerValue) => void;
  canWrite: boolean;
  /**
   * Versions of the servers this will be sent to, when it belongs to a
   * schedule. Families those servers need but that are switched off are
   * called out; omit it for a library entry, which has no targets.
   */
  targetVersions?: (string | null)[];
  /** The viewer's own Minecraft name, prefilled for "Test in game". */
  defaultPlayer?: string | null;
  /** Extra buttons for the header, e.g. library actions. */
  actions?: ReactNode;
  className?: string;
}

const EXAMPLES = [
  'A “STORE SALE” heading in a gold-to-orange gradient, then “20% off ranks this weekend” with a link to store.playcdu.co',
  'A vote reminder: “Vote for CDU” in green, and hovering it lists the rewards',
  'Our Discord invite in bold blue, and clicking it opens discord.gg/playcdu',
];

const SELECTOR = /^(@[aprse](\[[^\]]*\])?|[A-Za-z0-9_]{3,16})$/;
const MAX_TURNS_SENT = 24;

const hasRaw = (message: TellrawMessage) => message.runs.some((run) => 'raw' in run);

export const TellrawComposer = ({
  value,
  onChange,
  canWrite,
  targetVersions,
  defaultPlayer,
  actions,
  className,
}: TellrawComposerProps) => {
  const [input, setInput] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [jsonOpen, setJsonOpen] = useState(false);
  const [jsonText, setJsonText] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [copied, setCopied] = useState(false);

  // The message each assistant reply produced, so any earlier version can be
  // brought back. Kept for this visit only; the saved conversation is text.
  const snapshots = useRef(new Map<number, TellrawMessage>());
  const latest = useRef(value);
  latest.current = value;
  const listRef = useRef<HTMLOListElement>(null);

  const compiled = useMemo(
    () => compileTellraw(value.message, value.compat, value.selector || '@a'),
    [value.message, value.compat, value.selector],
  );
  const issues = useMemo(() => validateMessage(value.message), [value.message]);
  const misses = useMemo(
    () => (targetVersions ? compatMisses(value.compat, targetVersions) : []),
    [value.compat, targetVersions],
  );
  const editable = !hasRaw(value.message);
  const selectorValid = SELECTOR.test(value.selector.trim());
  const empty = value.message.runs.length === 0;

  useEffect(() => {
    listRef.current?.lastElementChild?.scrollIntoView({ block: 'nearest' });
  }, [value.conversation.length]);

  const send = async (prompt: string) => {
    const text = prompt.trim();
    if (!text || pending || !editable) return;
    const base = latest.current;
    const messages: ComposerTurn[] = [...base.conversation, { role: 'user', content: text }];
    setPending(true);
    setError(null);
    try {
      const reply = await scheduledCommandsApi.compose({
        messages: messages.slice(-MAX_TURNS_SENT),
        draft: base.message.runs.length ? base.message : null,
        compat: base.compat,
      });
      const conversation: ComposerTurn[] = [...messages, { role: 'assistant', content: reply.reply }];
      snapshots.current.set(conversation.length - 1, reply.message);
      // Compatibility may have been changed while the reply was on its way;
      // keep whatever is current and take only the message and the turns.
      onChange({ ...latest.current, message: reply.message, conversation });
      setInput('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The assistant did not answer.');
    } finally {
      setPending(false);
    }
  };

  const openJson = () => {
    setJsonText(JSON.stringify(compiled.component, null, 2));
    setJsonError(null);
    setJsonOpen(true);
  };

  const applyJson = () => {
    const parsed = parseTellraw(jsonText);
    if (!parsed.ok) {
      setJsonError(parsed.line ? `${parsed.error} (line ${parsed.line}, column ${parsed.column ?? 1})` : parsed.error);
      return;
    }
    onChange({ ...value, message: parsed.message, selector: parsed.selector ?? value.selector });
    setJsonOpen(false);
  };

  const copyCommand = async () => {
    try {
      await navigator.clipboard.writeText(compiled.command);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setError('Your browser did not allow copying. Open the JSON and copy it from there.');
    }
  };

  const currentTurn = value.conversation.length - 1;

  return (
    <div className={cn('space-y-4', className)}>
      {/* Preview */}
      <div className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="eyebrow">What players see</span>
          <div className="flex flex-wrap items-center gap-1">
            {actions}
            <button type="button" onClick={copyCommand} className="btn-ghost btn-xs" disabled={empty} title="Copy the whole tellraw command">
              {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              {copied ? 'Copied' : 'Copy command'}
            </button>
            {canWrite && (
              <button type="button" onClick={() => setTesting(true)} className="btn-ghost btn-xs" disabled={empty || issues.length > 0}>
                <UserRound className="w-3.5 h-3.5" />
                Test in game
              </button>
            )}
          </div>
        </div>
        <TellrawPreview
          component={compiled.component}
          emptyLabel={canWrite ? 'Describe a message below and it will appear here.' : 'This message is empty.'}
          className="min-h-[3rem]"
        />
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <CompatToggles value={value.compat} onChange={(compat) => onChange({ ...value, compat })} disabled={!canWrite} />
          {!empty && <span className="text-xs text-glass-500 tabular-nums">{compiled.bytes.toLocaleString()} bytes</span>}
        </div>
      </div>

      {/* What the chosen versions change, and what they cannot do */}
      {(misses.length > 0 || compiled.notes.length > 0 || issues.length > 0) && (
        <ul className="space-y-1.5" aria-label="Compatibility notes">
          {misses.map((miss) => (
            <li key={miss.family} className="alert alert-warning py-2 text-xs">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              <span>
                {FAMILY_LABELS[miss.family]} is off, but {miss.count} targeted server{miss.count === 1 ? '' : 's'} run{miss.count === 1 ? 's' : ''}{' '}
                {miss.versions.join(', ')}. They will reject this message.
              </span>
            </li>
          ))}
          {issues.map((issue, i) => (
            <li key={`issue-${i}`} className="alert alert-danger py-2 text-xs">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{issue.message}</span>
            </li>
          ))}
          {compiled.notes.map((note) => (
            <li key={`${note.kind}-${note.message}`} className="flex items-start gap-2 text-xs text-glass-400">
              {note.kind === 'rcon_length' ? (
                <AlertTriangle className="w-3.5 h-3.5 mt-px shrink-0 text-amber-400" />
              ) : (
                <Info className="w-3.5 h-3.5 mt-px shrink-0 text-glass-500" />
              )}
              <span>{note.message}</span>
            </li>
          ))}
        </ul>
      )}

      {/* Conversation */}
      <div className="subcard">
        {value.conversation.length > 0 ? (
          <ol ref={listRef} className="max-h-72 overflow-y-auto divide-y divide-glass-200/10" aria-label="Conversation with the assistant">
            {value.conversation.map((turn, index) => {
              const snapshot = snapshots.current.get(index);
              return (
                <li key={index} className="flex items-start gap-3 px-3 py-2.5">
                  <span className={cn('w-16 shrink-0 text-xs font-medium pt-px', turn.role === 'user' ? 'text-glass-500' : 'text-accent-300')}>
                    {turn.role === 'user' ? 'You' : 'Assistant'}
                  </span>
                  <p className="flex-1 min-w-0 text-sm text-white whitespace-pre-wrap break-words">
                    {turn.content}
                  </p>
                  {turn.role === 'assistant' && snapshot && (
                    index === currentTurn && snapshot === value.message ? (
                      <span className="badge badge-neutral shrink-0">Current</span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => onChange({ ...value, message: snapshot })}
                        className="btn-ghost btn-xs shrink-0"
                        disabled={!canWrite}
                        title="Bring back the message as it was after this reply"
                      >
                        <Undo2 className="w-3.5 h-3.5" />
                        Restore
                      </button>
                    )
                  )}
                </li>
              );
            })}
          </ol>
        ) : (
          canWrite && editable && (
            <div className="px-3 py-3 space-y-2">
              <p className="text-sm text-glass-300">
                Describe what players should see. Follow-up messages change the current version, so you can say
                “make the link blue” or “shorter”.
              </p>
              <div className="flex flex-wrap gap-1.5">
                {EXAMPLES.map((example) => (
                  <button
                    key={example}
                    type="button"
                    onClick={() => setInput(example)}
                    className="chip text-left hover:border-glass-200/30 transition-colors"
                  >
                    {example}
                  </button>
                ))}
              </div>
            </div>
          )
        )}

        {canWrite && (
          <div className={cn('p-3', (value.conversation.length > 0 || editable) && 'border-t border-glass-200/10')}>
            {editable ? (
              <form
                className="flex items-end gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  void send(input);
                }}
              >
                <label htmlFor="composer-input" className="sr-only">Message to the assistant</label>
                <textarea
                  id="composer-input"
                  className="glass-input flex-1 min-h-[2.75rem] max-h-40 resize-y py-2.5"
                  rows={value.conversation.length > 0 ? 1 : 2}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                      e.preventDefault();
                      void send(input);
                    }
                  }}
                  placeholder={empty ? 'Describe the message…' : 'Say what to change…'}
                  disabled={pending}
                  maxLength={4000}
                />
                <button type="submit" className="glass-button shrink-0" disabled={pending || !input.trim()}>
                  {pending ? <Loader className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  <span className="hidden sm:inline">{pending ? 'Writing…' : 'Send'}</span>
                </button>
              </form>
            ) : (
              <p className="text-xs text-glass-400">
                The assistant cannot edit this message: it contains parts from an import (a scoreboard value, a selector
                or a translation) that it could not give back unchanged. Edit the JSON instead, or start a new message.
              </p>
            )}
            {error && (
              <p className="mt-2 flex items-start gap-1.5 text-xs text-red-400" role="alert">
                <AlertCircle className="w-3.5 h-3.5 mt-px shrink-0" />
                {error}
              </p>
            )}
            <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs text-glass-500">
              <span className="hidden md:inline">
                <span className="kbd">Enter</span> to send, <span className="kbd">Shift</span> + <span className="kbd">Enter</span> for a new line
              </span>
              <div className="flex items-center gap-1 ml-auto">
                <button type="button" onClick={jsonOpen ? () => setJsonOpen(false) : openJson} className="btn-ghost btn-xs">
                  <Code2 className="w-3.5 h-3.5" />
                  {jsonOpen ? 'Close JSON' : 'Edit JSON'}
                </button>
                {(value.conversation.length > 0 || !empty) && (
                  <button type="button" onClick={() => setConfirmReset(true)} className="btn-ghost btn-xs">
                    <RotateCcw className="w-3.5 h-3.5" />
                    Start over
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Hand-editing, for the rare case the assistant is not the right tool */}
      {jsonOpen && canWrite && (
        <div className="space-y-2">
          <div className="flex flex-wrap items-end justify-between gap-2">
            <div>
              <label htmlFor="composer-json" className="block text-xs font-medium text-glass-400">
                Text component
              </label>
              <p className="text-xs text-glass-500 mt-0.5">
                Paste JSON or a whole tellraw command. Applying replaces the message with exactly this, so gradients
                become individual colours.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <label htmlFor="composer-selector" className="text-xs font-medium text-glass-400">Shown to</label>
              <input
                id="composer-selector"
                className={cn('glass-input w-32 font-mono text-xs min-h-[2rem] py-1', !selectorValid && 'border-red-500/50')}
                value={value.selector}
                onChange={(e) => onChange({ ...value, selector: e.target.value })}
                spellCheck={false}
                title="A target selector such as @a, or one player's name"
              />
            </div>
          </div>
          <textarea
            id="composer-json"
            className="glass-input w-full font-mono text-xs leading-5 min-h-[12rem]"
            value={jsonText}
            onChange={(e) => setJsonText(e.target.value)}
            spellCheck={false}
          />
          {jsonError && (
            <p className="flex items-start gap-1.5 text-xs text-red-400" role="alert">
              <AlertCircle className="w-3.5 h-3.5 mt-px shrink-0" />
              {jsonError}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setJsonOpen(false)} className="glass-button-secondary">Cancel</button>
            <button type="button" onClick={applyJson} className="glass-button" disabled={!jsonText.trim()}>
              Apply JSON
            </button>
          </div>
        </div>
      )}

      {testing && (
        <PlayerSendModal
          title="Test in game"
          subtitle="Shows this message to one player so you can see it for real."
          defaultPlayer={defaultPlayer}
          renderPreview={() => <TellrawPreview component={compiled.component} />}
          send={(player) => scheduledCommandsApi.test(player, compiled.component)}
          onClose={() => setTesting(false)}
        />
      )}

      <ConfirmationModal
        isOpen={confirmReset}
        onClose={() => setConfirmReset(false)}
        onConfirm={() => {
          snapshots.current.clear();
          onChange({ ...value, message: { runs: [] }, conversation: [] });
          setConfirmReset(false);
          setInput('');
        }}
        title="Start a new message?"
        message="The current message and the conversation are cleared. Anything already saved stays as it is until you save again."
        confirmText="Start over"
      />
    </div>
  );
};
